import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import axios from 'axios';
import { Sentry } from './sentry';
import { trackPageView, trackUserEvent } from './analytics';
import Auth from './components/Auth';
import Chat from './components/Chat';
import UpgradeToPremium from './components/UpgradeToPremium';
import HowItWorksModal from './components/HowItWorksModal';
import CookieConsent from './components/CookieConsent';
import FAQ from './pages/FAQ';
import Footer from './components/Footer';
import UserMenu from './components/UserMenu';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Settings from './pages/Settings';
import { theme, buttonStyles, typography, mergeStyles, spacing, isMobile, mobileSpacing, borderRadius } from './styles/theme';
import './App.css';
import About from './pages/About';
import ConversationSidebar from './components/ConversationSidebar';
import ConversationDrawer from './components/ConversationDrawer';

function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view whenever route changes
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null; // This component doesn't render anything
}

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showConversationDrawer, setShowConversationDrawer] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [refreshConversations, setRefreshConversations] = useState(0);

  // Track window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth) // Update state when window resizes
    };

    window.addEventListener('resize', handleResize); // Add listener
    return () => window.removeEventListener('resize', handleResize); // Cleanup on unmount
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        trackUserEvent('user_login', {
          method: currentUser.providerData[0]?.providerId || 'unknown',
        });
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setUserData(null);
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/profile`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const userData = {
          ...response.data,
          isPremium: response.data.status === 'premium'
        };
        setUserData(userData);
        if (user) {
          user.isPremium = userData.isPremium; // Update user object with isPremium
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [user]);

  // Show "How it Works" modal on first visit
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenJugyIntro');
    if (!hasSeenIntro) {
      setShowHowItWorks(true);
      localStorage.setItem('hasSeenJugyIntro', 'true');
    }
  }, []);

  useEffect(() => {
    const shouldShowUpgrade = localStorage.getItem('showUpgradeModal');
    if (shouldShowUpgrade === 'true') {
      setShowUpgrade(true);
      localStorage.removeItem('showUpgradeModal');
    }
  }, []);

  const handleSignOut = () => {
    signOut(auth);
    setShowAuth(false);
  };

  const handleSelectConversation = (convId) => {
    setCurrentConversationId(convId);
  };

  const handleNewConversation = () => {
    setCurrentConversationId(null);
  };

  const handleConversationCreated = (convId) => {
    setCurrentConversationId(convId);
    setRefreshConversations(prev => prev +1); // Trigger refresh
  };

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        marginTop: '100px',
        color: theme.text.primary 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <PageViewTracker />
      <Routes>
        {/* Success/Cancel pages - no header */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancel" element={<PaymentCancel />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/settings" element={<Settings user={user} userData={userData} />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />

        {/* Main app - with header */}
        <Route path="/" element={
          <div className="App" style={{ 
            backgroundColor: theme.bg.primary, 
            minHeight: '100vh',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              padding: '15px 30px',
              backgroundColor: theme.bg.dark,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: theme.shadow.small,
              zIndex: 1000,
              ...(isMobile() && {
                padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
                flexWrap: 'nowrap', // Keep items on one line
              })
            }}>
              {/* Left side: Logo + How It Works */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.md,
                flex: 1,
                minWidth: 0, // Allow flex shrinking
                ...(isMobile() && {
                  gap: mobileSpacing.sm,
                })
              }}>
                <img 
                  src="/logo-light.svg" 
                  alt="Judgy - Magic: The Gathering Rules Assistant"
                  style={{
                    height: '40px',
                    width: 'auto',
                    cursor: 'pointer',
                    flexShrink: 0,
                    ...(isMobile() && {
                      height: '28px', // Smaller on mobile to fit
                    })
                  }}
                  onClick={() => window.location.href = '/'}
                />
              
                {/* History button for mobile premium users */}
                {isMobile() && user && userData?.isPremium && (
                  <button
                    onClick={() => setShowConversationDrawer(true)}
                    style={{
                      background: 'none',
                      border: `1px solid ${theme.border.light}`,
                      borderRadius: borderRadius.sm,
                      padding: mobileSpacing.sm,
                      cursor: 'pointer',
                      color: theme.text.inverse,
                      fontSize: typography.fontSize.sm,
                      minWidth: '44px',
                      minHeight: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Conversation History"
                  >
                    📜
                  </button>
                )}

                {/* How It Works button - hide on very small screens */}
                {(!isMobile() || window.innerWidth >= 500) && (
                  <button
                    onClick={() => setShowHowItWorks(true)}
                    style={mergeStyles(
                      buttonStyles.base,
                      buttonStyles.secondary,
                      buttonStyles.small,
                      {
                        backgroundColor: 'transparent',
                        color: theme.text.inverse,
                        border: `2px solid ${theme.border.light}`,
                        fontSize: typography.fontSize.sm,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        ...(isMobile() && {
                          fontSize: typography.mobileFontSize.xs,
                          padding: `${mobileSpacing.xs} ${mobileSpacing.sm}`,
                        })
                      }
                    )}
                  >
                    How It Works
                  </button>
                )}
              </div>

              {/* Right side: User Menu or Sign In */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}>
                {user ? (
                  <UserMenu
                    user={user}
                    onLogout={handleSignOut}
                    onSettings={() => window.location.href = '/settings'}
                    onUpgrade={() => setShowUpgrade(true)}
                    onHowItWorks={() => setShowHowItWorks(true)}
                  />
                ) : (
                  <button
                    onClick={() => setShowAuth(true)}
                    style={mergeStyles(
                      buttonStyles.base,
                      buttonStyles.primary,
                      buttonStyles.small,
                      {
                        whiteSpace: 'nowrap',
                        ...(isMobile() && {
                          fontSize: typography.mobileFontSize.sm,
                          padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
                        })
                      }
                    )}
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>

            <div style={{
              height: '60px',
              ...(isMobile() && { height: '90px' })
              }}></div> {/* Spacer for fixed header */}

            <div style={{
              flex: 1,
              display: 'flex',
              overflow: 'hidden',
              width: '100%',
            }}>
              {/* Sidebar for desktop premium users */}
              {!isMobile() && user && userData?.isPremium && (
                <ConversationSidebar
                  user={user}
                  currentConversationId={currentConversationId}
                  onSelectConversation={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                  isOpen={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  refreshTrigger={refreshConversations}
                />
              )}
              
              {/* Main content */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
                <Chat 
                  user={user} 
                  userData={userData}
                  currentConversationId={currentConversationId}
                  onConversationChange={handleSelectConversation}
                  onConversationCreated={handleConversationCreated}
                />
                <Footer />
              </div>
            </div>

            {/* Drawer for mobile premium users */}
            {isMobile() && user && userData?.isPremium && (
              <ConversationDrawer
                user={user}
                currentConversationId={currentConversationId}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                isOpen={showConversationDrawer}
                onClose={() => setShowConversationDrawer(false)}
              />
            )}

            {/* Auth Modal */}
            {showAuth && !user && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                overflowY: 'auto',
              }} onClick={() => setShowAuth(false)}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    backgroundColor: theme.bg.primary,
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '450px',
                    width: '90%',
                    boxShadow: theme.shadow.large,
                    ...(isMobile() && {
                      padding: mobileSpacing.lg,
                      width: '95%',
                      maxWidth: '100%',
                      borderRadius: borderRadius.lg,
                      margin: mobileSpacing.md,
                    })
                  }}
                >
                  <button
                    onClick={() => setShowAuth(false)}
                    style={{
                      float: 'right',
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: theme.text.primary,
                      minWidth: '44px',
                      minHeight: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      ...(isMobile() && {
                        minWidth: '48px',
                        minHeight: '48px',
                      })
                    }}
                  >
                    ×
                  </button>
                  <Auth onSuccess={() => setShowAuth(false)} />
                </div>
              </div>
            )}

            {/* How It Works Modal */}
            {showHowItWorks && (
              <HowItWorksModal onClose={() => setShowHowItWorks(false)} />
            )}
            {showUpgrade && user && !user.isPremium && (
              <UpgradeToPremium
                user={user}
                onClose={() => setShowUpgrade(false)}
                autoOpen={true}
              />
            )}
            <CookieConsent />
          </div>
        } />
      </Routes>
    </Router>
  );
}

function AppWithErrorBoundary() {
  return (
    <Sentry.ErrorBoundary 
      fallback={({ error, resetError }) => (
        // Custom error UI when app crashes
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.bg.primary,
          padding: spacing.xl,
          textAlign: 'center',
        }}>
          <h1 style={{
            color: theme.status.error,
            fontSize: typography.fontSize['3xl'],
            marginBottom: spacing.lg,
          }}>
            Oops! Something went wrong
          </h1>
          <p style={{
            color: theme.text.secondary,
            marginBottom: spacing.xl,
            maxWidth: '600px',
          }}>
            We've been notified and are looking into it. Please try refreshing the page.
          </p>
          <button
            onClick={resetError}
            style={mergeStyles(
              buttonStyles.base,
              buttonStyles.primary
            )}
          >
            Try Again
          </button>
          {process.env.NODE_ENV === 'development' && (
            <details style={{ 
              marginTop: spacing.xl,
              textAlign: 'left',
              maxWidth: '800px',
            }}>
              <summary style={{ cursor: 'pointer', marginBottom: spacing.md }}>
                Error Details (dev only)
              </summary>
              <pre style={{
                backgroundColor: theme.bg.dark,
                color: theme.text.inverse,
                padding: spacing.md,
                borderRadius: borderRadius.md,
                overflow: 'auto',
                fontSize: typography.fontSize.sm,
              }}>
                {error.toString()}
              </pre>
            </details>
          )}
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(AppWithErrorBoundary);